import {Pay} from 'com/gongxigames/math/core/game/Pay';
import {Reel} from 'com/gongxigames/math/core/game/Reel';
import {Container, DisplayObject, Point, Sprite} from 'pixi.js';
import {CoreDI, DisplayObjectsPool, ICachedDisplayObject, inject, Map, ObjectBuilder} from '@gongxigames/framework/core';
import {GameConfig, IReelsScaleService} from '@gongxigames/framework/slot/types';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {ReelConstants, ReelsOrdering, SymbolPosition} from '@gongxigames/framework/slot/utils';
import {ReelView} from '@gongxigames/framework/slot/modules/reels/ReelView';
import {ReelsViewEvents} from '@gongxigames/framework/slot/modules/reels/ReelsViewEvents';
import {AUTOMATION_META, AutomationMetaAny} from '@gongxigames/framework/slot/fragments/Autotest';
import {View} from '@gongxigames/framework/slot/fragments/module/view';

type ReelsIteratorCallback = (value: number) => void;

export abstract class AbstractReelsView extends View {
    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    @inject(DI.ReelsScaleService)
    protected _reelsScaleService!: IReelsScaleService;

    protected _isQuickStop = false;
    protected _reels: ReelView[] = [];
    protected _stopPositions: number[] = [];
    protected _reelsetContainer!: Container;
    protected _reelSetAnimationContainer!: Container;
    protected _oversizedsOnStoppedReelsLayer!: Container;
    protected _reelConstants!: typeof ReelConstants;
    protected _isBaseGame = true;
    protected _symbolsWithHitAnimationOnReels!: number[][][];
    protected _oversizeSymbolsOnReels!: number[][][];

    // represents matrix with scatters positions on reels
    protected _scattersOnReels: number[][] = [];

    protected _scattersNumber = 0;

    protected _symbolsPool: DisplayObjectsPool<Sprite> = new DisplayObjectsPool<Sprite>();

    public init(): void {
        super.init();

        this._reelConstants = ReelConstants;
        this.createCachedBlurredSymbols();

        this._symbolsPool
            .addFactoryFunction('symbols', () => {
                return this._objectBuilder.buildSprite(ObjectBuilder.resources.game_sprite.symbols);
            })
            .addFactoryFunction('reelsBg', () => {
                const reelsBg = ObjectBuilder.resources.game_sprite.reelsBg;

                return (reelsBg ? this._objectBuilder.buildSprite(reelsBg) : null)!;
            });

        (this as any)[AUTOMATION_META] = () => {
            const meta: AutomationMetaAny = {
                type: 'reels'
            };

            this.reels.forEach((reel, i) => {
                meta[`reel${i}_isStopped`] = reel.isStopped;
            });

            return meta;
        };
    }

    public createCachedBlurredSymbols(): void {
        const {blur} = this._gameConfig.reelsConfig;

        if (!blur) {
            return;
        }

        const symbolsSprite: Sprite = this._objectBuilder.buildSprite(ObjectBuilder.resources.game_sprite.symbols);
        const cachedSymbols: Map<ICachedDisplayObject> = {};

        this._displayObjectUtils.addBlurFilter(symbolsSprite, blur);

        symbolsSprite.children.forEach(symbol => symbol.visible = false);
        symbolsSprite.children.forEach((symbol, index) => {
            symbol.visible = true;
            cachedSymbols[index] = this.generateSymbolTexture(symbolsSprite, new Point(0, blur));
            symbol.visible = false;
        });

        this._displayObjectUtils.addCachedDisplayObjects(cachedSymbols);
    }

    public generateSymbolTexture(container: DisplayObject, padding?: Point): ICachedDisplayObject {
        const {width, height} = this._reelConstants.SYMBOL;
        const bounds = container.getBounds();

        if (padding) {
            bounds.x -= padding.x;
            bounds.y -= padding.y;
        }

        container.position.x -= bounds.x;
        container.position.y -= bounds.y;

        const region = container.getBounds();

        if (padding) {
            // increase texture size for filters
            // that distort the image and image size
            bounds.x -= padding.x;
            bounds.width += padding.x * 2;
            bounds.y -= padding.y;
            bounds.height += padding.y * 2;
        }

        return {
            position: new Point(width / 2 - bounds.x, height / 2 + bounds.y),
            texture: this._displayObjectUtils.generateTexture(container, region)
        };
    }

    public startSpinning(): void {
        this._isQuickStop = false;
        this.setupReelSetMaskForSpinning();
        this.removeOversizedsFromSpecialLayer();

        for (const reel of this._reels) {
            reel.startSpinning();
        }
    }

    public tryStoppingAllReels(): void {
        for (let reelIndex = 0; reelIndex < this._reels.length; reelIndex++) {
            this.onStopReel(reelIndex);
        }
    }

    public killEverythingFromAnimationsLayer(): void {
        this.stopSymbolHitAnimations();

        this._reelSetAnimationContainer.children.length = 0;

        // remove all reel animations and evoke callbacks
        this._reels.forEach(reel => reel.animationUpdater.removeAll());
        this.onLastReelAnimationFinished();
    }

    public stopSymbolHitAnimations(): void {
        for (const reel of this._reels) {
            reel.stopSymbolHitAnimations();
        }
    }

    public tryStoppingReel(reelIndex: number): void {
        this.onStopReel(reelIndex);
    }

    public hidePays(pays: Pay[]): void {
        for (const pay of pays) {
            for (const pos of pay.positions) {
                this.hideSymbol({x: pos.reel, y: pos.row});
            }
        }
    }

    public iterateReels(reelsAmount: number, callBack: ReelsIteratorCallback): void {
        if (this._reelConstants.REELS_ORDERING === ReelsOrdering.LEFT_TO_RIGHT) {
            for (let reelIndex = 0; reelIndex < reelsAmount; reelIndex++) {
                callBack(reelIndex);
            }
        } else if (this._reelConstants.REELS_ORDERING === ReelsOrdering.RIGHT_TO_LEFT) {
            for (let reelIndex = reelsAmount - 1; reelIndex >= 0; reelIndex--) {
                callBack(reelIndex);
            }
        }
    }

    public hideSymbol(position: SymbolPosition): void {
        this._reels[position.x].hideSymbolAt(position.y);
    }

    public showSymbol(position: SymbolPosition): void {
        this._reels[position.x].showSymbolAt(position.y);
    }

    public replaceSymbol(position: SymbolPosition, symbolId: number): void {
        this._reels[position.x].replaceSymbolAt(position.y, symbolId);
    }

    public showAllSymbols(): void {
        for (const reel of this._reels) {
            for (let row = 0; row < this.reelConstants.ROW_AMOUNT; row++) {
                reel.showSymbolAt(row);
            }
        }
    }

    public hideAllSymbols(): void {
        for (const reel of this._reels) {
            for (let row = 0; row < this.reelConstants.ROW_AMOUNT; row++) {
                reel.hideSymbolAt(row);
            }
        }
    }

    public setupReelStrip(reels: Reel[], reelConstants?: typeof ReelConstants): void {
        if (reelConstants) {
            this.reelConstants = reelConstants;
        }

        this.createReelSet(reels, undefined);
    }

    public setupReelStripWithPositions(reels: Reel[], positions: number[]): void {
        this.createReelSet(reels, positions);
    }

    public updateRubyCoinOnRestoredReels(reelIndex: number, rowIndex: number): void {
        this._reels[reelIndex].updateRubyCoinOnRestore(rowIndex);
    }

    public onOrientationChanged(): void {
        super.onOrientationChanged();

        this._reelsScaleService.scaleByCurrentScreenOrientation(this.container);
    }

    public abstract reOrder(): void;

    public abstract setReelsBackground(): void;

    protected rowIndexToSymbolIndex(rowIndex: number): number {
        return rowIndex + Math.abs(this._reelConstants.Y_ADDITIONAL_SYMBOLS_OFFSET);
    }

    protected putOversizedsOnSpecialLayer(reelNr: number): void {
        this.defaultSymbolsOrder.forEach( (orderSymbolType) => {
            const reel = this._reels[reelNr];

            reel.iterateRows((rowIndex) => {
                const symbol = reel.reelSymbols[this.rowIndexToSymbolIndex(rowIndex)];
                const rowSymbolType = symbol.symbolType;

                if (this.isOversized(rowSymbolType) && this.isSymbolsEqual(orderSymbolType, rowSymbolType)) {
                    this._oversizedsOnStoppedReelsLayer.addChild(symbol);
                }
            }, true);
        });
    }

    protected isSymbolsEqual(orderSymbol: number | number[], rowSymbol: number): boolean {
        if (orderSymbol instanceof Array) {
            return orderSymbol.indexOf(rowSymbol) !== -1;
        }

        return orderSymbol === rowSymbol;
    }

    public restoreOversizedSymbolsOnSpecialLayer(): void {
        for (let reel = 0; reel < this.reelConstants.REELS_AMOUNT; reel++) {
            this.putOversizedsOnSpecialLayer(reel);
        }
    }

    protected onStopReel(reelIndex: number): void {
        this._reels[reelIndex].symbolsWithHitAnimation = this._symbolsWithHitAnimationOnReels[reelIndex];
        this._reels[reelIndex].oversizeHitSymbols = this.getOversizeHitSymbolsOnReel(reelIndex);
        this._reels[reelIndex].startStopping(this._stopPositions[reelIndex]);
    }

    protected getOversizeHitSymbolsOnReel(reelIndex: number): number[] {
        const symbolsOnRows: number[] = [];
        const isScatter = (rows: number[], symbolType: number): boolean => {
            let value = false;

            if (this._scattersOnReels?.[reelIndex]) {
                const scattersOnReel = this._scattersOnReels[reelIndex];

                for (const row of rows) {
                    value = !!scattersOnReel[row] && scattersOnReel[row] === symbolType;
                }
            }

            return value;
        };

        this._oversizeSymbolsOnReels?.[reelIndex]?.forEach((rows, symbolType) => {
            if (this.symbolsWithHitAnimation.indexOf(symbolType) >= 0 || isScatter(rows, symbolType)) {
                return;
            }

            rows.forEach((row) => {
                symbolsOnRows[row] = symbolType;
            });
        });

        return symbolsOnRows;
    }

    protected onLastReelAnimationFinished(): void {
        this.reOrder();
        this.emit(ReelsViewEvents.ALL_REEL_ANIMATIONS_FINISHED);
    }

    public isReelStopped(reelIndex: number): boolean {
        return this._reels[reelIndex].isStopped;
    }

    protected isOversized(symbolType: number): boolean {
        return this.oversizeSymbols.indexOf(symbolType) !== -1;
    }

    public toggleTurboSpin(value: boolean): void {
        this._reels.forEach(reel => reel.toggleTurboSpin(value));
    }

    protected abstract createReelSet(reels: Reel[], reelsPosition?: number[]): void;

    protected abstract setupReelSetMaskForSpinning(): void;

    protected abstract removeOversizedsFromSpecialLayer(): void;

    public set isQuickStop(value: boolean) {
        this._isQuickStop = value;
    }

    public get isQuickStop(): boolean {
        return this._isQuickStop;
    }

    public get reelConstants(): typeof ReelConstants {
        return this._reelConstants;
    }

    public set reelConstants(value: typeof ReelConstants) {
        this._reelConstants = value;
    }

    public get stopPositions(): number[] {
        return this._stopPositions;
    }

    public set stopPositions(value: number[]) {
        this._stopPositions = value;
    }

    public set scattersOnReels(value: number[][]) {
        this._scattersOnReels = value;
    }

    public get scattersNumber(): number {
        return this._scattersNumber;
    }

    public set scattersNumber(value: number) {
        this._scattersNumber = value;
    }

    public get isBaseGame(): boolean {
        return this._isBaseGame;
    }

    public get reels(): ReelView[] {
        return this._reels;
    }

    public set isBaseGame(value: boolean) {
        this._isBaseGame = value;
    }

    public get symbolsWithHitAnimationOnReels(): number[][][] {
        return this._symbolsWithHitAnimationOnReels;
    }

    public set symbolsWithHitAnimationOnReels(value: number[][][]) {
        this._symbolsWithHitAnimationOnReels = value;
    }

    public get oversizeSymbolsOnReels(): number[][][] {
        return this._oversizeSymbolsOnReels;
    }

    public set oversizeSymbolsOnReels(value: number[][][]) {
        this._oversizeSymbolsOnReels = value;
    }

    public get scatterIndex(): number {
        return this._gameConfig.reelsConfig.scatterIndex!;
    }

    public get defaultSymbolsOrder(): (number | number[])[] {
        return this._gameConfig.reelsConfig.defaultSymbolsOrder;
    }

    public get oversizeSymbols(): number[] {
        return this._gameConfig.reelsConfig.oversizeSymbols;
    }

    public get symbolsWithHitAnimation(): number[] {
        return this._gameConfig.reelsConfig.symbolsWithHitAnimation;
    }

    public get gameConfig(): GameConfig {
        return this._gameConfig;
    }
}
